import { useEffect, useState } from "react";
import type { AudioClipInstance } from "@/api/client";
import type { CommitClipsFn } from "@/hooks/useClipHistory";

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * All mouse-drag interactions on the timeline:
 *   • Clip move (drag along time axis)
 *   • Right-edge resize (change duration)
 *   • Left-edge resize (trim head — moves start + source_offset together)
 *
 * Takes `commitClipsFnRef` (not commitClips directly) to stay free of the
 * initialisation order constraint with useClipMutations.
 */
export function useClipDrag(
  pxPerSec:            number,
  clipsRef:            React.MutableRefObject<AudioClipInstance[]>,
  patchClipLive:       (id: string, patch: Partial<AudioClipInstance>) => void,
  triggerUpdateStatus: () => void,
  capturePending:      () => void,
  flushPending:        () => void,
  discardPending:      () => void,
  commitClipsFnRef:    React.MutableRefObject<CommitClipsFn>,
  setSelectedClipId:   (id: string | null) => void,
) {
  const [durationDrag,    setDurationDrag]    = useState<{ id: string; origDurationSec: number; origMouseX: number } | null>(null);
  const [startResizeDrag, setStartResizeDrag] = useState<{ id: string; origStartSec: number; origOffsetSec: number; origEndSec: number; origMouseX: number } | null>(null);
  const [clipDrag,        setClipDrag]        = useState<{ id: string; origStartSec: number; origMouseX: number } | null>(null);

  // ── Right-edge resize ────────────────────────────────────────────────────────

  const handleDurationResizeStart = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const clip = clipsRef.current.find((c) => c.id === id);
    if (!clip) return;
    capturePending();
    setSelectedClipId(id);
    setDurationDrag({ id, origDurationSec: clip.duration_seconds, origMouseX: e.clientX });
  };

  useEffect(() => {
    if (!durationDrag) return;
    const { id, origDurationSec, origMouseX } = durationDrag;
    let moved = false;
    document.body.style.cursor    = "ew-resize";
    document.body.style.userSelect = "none";

    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - origMouseX;
      if (Math.abs(dx) > 2) moved = true;
      patchClipLive(id, { duration_seconds: Math.max(0.5, origDurationSec + dx / pxPerSec) });
    };
    const onUp = () => {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
      setDurationDrag(null);
      if (!moved) { discardPending(); return; }
      flushPending();
      triggerUpdateStatus();
      commitClipsFnRef.current(clipsRef.current, { remix: true });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationDrag, pxPerSec]);

  // ── Left-edge resize ─────────────────────────────────────────────────────────

  const handleStartResizeStart = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const clip = clipsRef.current.find((c) => c.id === id);
    if (!clip) return;
    capturePending();
    setSelectedClipId(id);
    setStartResizeDrag({
      id,
      origStartSec:  clip.start_seconds,
      origOffsetSec: clip.source_offset_seconds,
      origEndSec:    clip.start_seconds + clip.duration_seconds,
      origMouseX:    e.clientX,
    });
  };

  useEffect(() => {
    if (!startResizeDrag) return;
    const { id, origStartSec, origOffsetSec, origEndSec, origMouseX } = startResizeDrag;
    let moved = false;
    document.body.style.cursor    = "ew-resize";
    document.body.style.userSelect = "none";

    const onMove = (e: MouseEvent) => {
      const dx       = e.clientX - origMouseX;
      if (Math.abs(dx) > 2) moved = true;
      const minStart = Math.max(0, origStartSec - origOffsetSec);
      const newStart = Math.max(minStart, Math.min(origEndSec - 0.5, origStartSec + dx / pxPerSec));
      const delta    = newStart - origStartSec;
      patchClipLive(id, {
        start_seconds:         newStart,
        source_offset_seconds: Math.max(0, origOffsetSec + delta),
        duration_seconds:      origEndSec - newStart,
      });
    };
    const onUp = () => {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
      setStartResizeDrag(null);
      if (!moved) { discardPending(); return; }
      flushPending();
      triggerUpdateStatus();
      commitClipsFnRef.current(clipsRef.current, { remix: true });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startResizeDrag, pxPerSec]);

  // ── Clip move drag ───────────────────────────────────────────────────────────

  const handleClipDragStart = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const clip = clipsRef.current.find((c) => c.id === id);
    if (!clip) return;
    capturePending();
    setSelectedClipId(id);
    setClipDrag({ id, origStartSec: clip.start_seconds, origMouseX: e.clientX });
  };

  // Body cursor feedback
  useEffect(() => {
    if (clipDrag) {
      document.body.style.cursor    = "grabbing";
      document.body.style.userSelect = "none";
    } else {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
    }
    return () => {
      document.body.style.cursor    = "";
      document.body.style.userSelect = "";
    };
  }, [clipDrag]);

  useEffect(() => {
    if (!clipDrag) return;
    const { id, origStartSec, origMouseX } = clipDrag;
    let moved = false;
    const onMove = (e: MouseEvent) => {
      const dx = e.clientX - origMouseX;
      if (Math.abs(dx) > 2) moved = true;
      patchClipLive(id, { start_seconds: Math.max(0, origStartSec + dx / pxPerSec) });
    };
    const onUp = () => {
      setClipDrag(null);
      if (!moved) { discardPending(); return; }
      flushPending();
      triggerUpdateStatus();
      commitClipsFnRef.current(clipsRef.current, { remix: true });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clipDrag, pxPerSec]);

  return { clipDrag, handleDurationResizeStart, handleStartResizeStart, handleClipDragStart };
}
